import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, MessageCircle } from "lucide-react";

const LINKS = [
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Estimator", href: "#estimator", testid: "nav-link-calculator" },
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "Contact", href: "#contact", testid: "nav-link-contact" },
];

export default function Navbar({ scrollTo }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollTo(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0E17]/85 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group"
        >
          <span className="w-9 h-9 grid place-items-center bg-[#F59E0B] text-[#0A0E17] font-display font-extrabold text-sm tracking-tight rounded-sm group-hover:rotate-[-6deg] transition-transform duration-300">
            SI
          </span>
          <span className="font-display font-bold tracking-tight text-base sm:text-lg uppercase">
            Shivin <span className="text-[#F59E0B]">Infotech</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="font-mono2 text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-[#F59E0B] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="nav-cta-whatsapp"
            href="https://wa.me/917407037269"
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 grid place-items-center border border-white/15 rounded-full text-slate-300 hover:text-[#10B981] hover:border-[#10B981]/50 transition-colors duration-300"
            aria-label="WhatsApp"
          >
            <MessageCircle size={16} />
          </a>
          <a
            data-testid="nav-cta-call"
            href="tel:7407037269"
            className="flex items-center gap-2 bg-[#F59E0B] text-[#0A0E17] font-mono2 text-xs font-semibold tracking-wider px-5 py-2.5 rounded-full hover:bg-[#E65100] hover:text-white transition-colors duration-300"
          >
            <Phone size={14} /> 74070 37269
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden w-10 h-10 grid place-items-center border border-white/15 rounded-sm"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-[#0A0E17]/95 border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.href)}
                  className="text-left font-display font-bold text-2xl uppercase tracking-tight text-slate-100 hover:text-[#F59E0B] transition-colors duration-300"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="nav-cta-call-mobile"
                href="tel:7407037269"
                className="mt-2 flex items-center justify-center gap-2 bg-[#F59E0B] text-[#0A0E17] font-mono2 text-sm font-semibold px-5 py-3 rounded-full"
              >
                <Phone size={15} /> Call 74070 37269
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
